import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { PlannerService } from '../../../service/api/planner-service';
import { normalizeSavePlanWorkTicketFields } from '../../../service/api/planner-save-plan-body';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { fetchResources } from '../helpers';
import { channels } from '../helpers/channels';
import { MODAL_TYPES } from 'Feature/components/app-modals';

const { PROGRAM_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'planner';
export const INITIAL_STATE = {
  data: {
    channels: [],
    programs: [],
    elements: [],
  },
  loading: false,
  error: null,
  filters: {
    start: '',
    end: '',
     searchText: '',
    // status: ''   // new
  },
  fetchWorkTicketPending: false,
};

const handleResponse = async (promise, rejectWithValue) => {
  try {
    const response = await promise;
    if (response.status === 200) {
      return response;
    } else {
      return rejectWithValue(response.errorMessage?.message || 'Failed to process request');
    }
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to process request');
  }
};

export const fetchPlanner = createAsyncThunk(
  'planner/fetchPlanner',
  async (filters, { rejectWithValue }) => {
    const channelResponse = await handleResponse(PlannerService.getAllChannel(), rejectWithValue);
    const taskResponse = await handleResponse(PlannerService.getAllTask(), rejectWithValue);

    
    // Map filters to API expected fields
    // const taskFilters = {};
    // if (filters) {
    //   if (filters.fromDate) taskFilters.plannedStartDate = filters.fromDate + 'T00:00:00';
    //   if (filters.toDate) taskFilters.plannedEndDate = filters.toDate + 'T23:59:59';
    //   if (filters.status) taskFilters.status = filters.status;
    // }
    // const taskResponse = await handleResponse(
    //   PlannerService.getAllTask(),
    //   rejectWithValue,
    // );
    // const taskResponse = await handleResponse(PlannerService.getAllTask(), rejectWithValue);
    
    return [channelResponse.response || [], taskResponse.response];
  },
);

export const savePlan = createAsyncThunk('planner/savePlan', async (data, { rejectWithValue }) => {
  const payload = { ...data };
  normalizeSavePlanWorkTicketFields(payload);

  const userId = localStorage.getItem('UserId');
  if (userId != null && String(userId).trim() !== '') {
    payload.userId = String(userId).trim();
  } else {
    delete payload.userId;
    delete payload.UserId;
  }

  const idStr = payload.id != null ? String(payload.id).trim() : '';
  if (!/^\d+$/.test(idStr)) {
    const ticket = String(payload.title ?? '').trim();
    if (ticket) {
      payload.id = `W${ticket}`;
    }
  }

  if (payload.since != null) {
    payload.since = String(payload.since).replace(' ', 'T');
  }
  if (payload.till != null) {
    payload.till = String(payload.till).replace(' ', 'T');
  }

  return handleResponse(PlannerService.savePlan(payload), rejectWithValue);
});

export const updatePlan = createAsyncThunk(
  'planner/updatePlan',
  async (data, { rejectWithValue }) => {
    const payload = { ...data };
    normalizeSavePlanWorkTicketFields(payload);

    const userId = localStorage.getItem('UserId');
    if (userId != null && String(userId).trim() !== '') {
      payload.userId = String(userId).trim();
    } else {
      delete payload.userId;
      delete payload.UserId;
    }

    return handleResponse(PlannerService.savePlan(payload), rejectWithValue);
  },
);

export const updateProcess = createAsyncThunk(
  'planner/updateProcess',
  async (data, { rejectWithValue }) => {
    return handleResponse(PlannerService.updateProcess(data), rejectWithValue);
  },
);

export const fetchByWorkTicketId = createAsyncThunk(
  'planner/fetchByWorkTicketId',
  async (id, { rejectWithValue }) => {
    return handleResponse(PlannerService.getByWorkTicketId(id), rejectWithValue);
  },
);

export const deleteByWorkTicketId = createAsyncThunk(
  'planner/deleteByWorkTicketId',
  async (id, { rejectWithValue }) => {
    return handleResponse(PlannerService.deleteByWorkTicketId(id), rejectWithValue);
  },
);

export const fetchPlannerWithFilters = () => (dispatch, getState) => {
  const filters = getState().planner.filters;
  dispatch(
    fetchPlanner({
      searchText: filters.searchText,
      //new code(status filter)
     
      // start: filters.start,         // optional: include if your API supports
      // end: filters.end,             // optional
    }),
  );
};

export const { reducer, actions } = createSlice({
    //new code(status filter)
//const slice = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    // setFromDate: (state, action) => {
    //   state.filters.fromDate = action.payload;
    // },
    // setToDate: (state, action) => {
    //   state.filters.toDate = action.payload;
    // },

    //new
    // setStatus: (state, action) => {
    //   state.filters.status = action.payload;
    // },
    removeElement: (state, action) => {
      const id = action.payload;
      state.data.elements = state.data.elements.map(element =>
        element.id === id ? { ...element, deleted: true } : element,
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPlanner.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanner.fulfilled, (state, action) => {
        state.loading = false;
        state.data.channels = action.payload[0];
        state.data.programs = action.payload[1].programs;
        state.data.elements = action.payload[1].elements.map(element => ({
          ...element,
          duration: '120',
          deleted: false,
        }));
      })
      .addCase(fetchPlanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(savePlan.fulfilled, (state, action) => {
        state.data.programs = [...state.data.programs, action.payload.response];
      })
      .addCase(fetchByWorkTicketId.pending, state => {
        state.fetchWorkTicketPending = true;
      })
      .addCase(fetchByWorkTicketId.fulfilled, state => {
        state.fetchWorkTicketPending = false;
      })
      .addCase(fetchByWorkTicketId.rejected, state => {
        state.fetchWorkTicketPending = false;
      })
      .addCase(deleteByWorkTicketId.fulfilled, (state, action) => {
        state.data.elements = action.payload.response.elements;
        state.data.programs = action.payload.response.programs;
      })
      .addCase(updateProcess.fulfilled, (state, action) => {
        const updatedProcess = action.payload.response;
        state.data.programs = state.data.programs.map(program =>
          program.id === updatedProcess.id ? updatedProcess : program,
        );
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(fetchPlanner.rejected, savePlan.rejected, updateProcess.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});


// redux.listener.startListening({
//   matcher: isAnyOf(updatePlan.fulfilled),
//   effect: (action, listenerApi) => {
//     listenerApi.dispatch(closeModal(PROGRAM_ADD_EDIT));
//     listenerApi.dispatch(fetchPlanWithFilters());
//     listenerApi.dispatch(
//       showToast({
//         message: 'Plan Updated successfully',
//         type: 'success',
//       }),
//     );
//   },
// });

redux.listener.startListening({
  matcher: isAnyOf(fetchByWorkTicketId.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(PROGRAM_ADD_EDIT, action.payload.response));
  },
});



// export const actions = slice.actions;
// export const { setFilters, setSearchText, removeElement } = slice.actions;
// const reducer = slice.reducer;

// redux.injectReducer(STORE_KEY, reducer);

// export default reducer;

redux.listener.startListening({
  matcher: isAnyOf(updateProcess.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(PROGRAM_ADD_EDIT));
  },
});

redux.injectReducer(STORE_KEY, reducer);
